/**
 * Backend-ready HTTP client for cloud chat conversations.
 * Live path: authenticated `/api/conversations/*` → Firestore `user_conversations/{uid}/items/*`.
 */

import type {
  ApiEnvelope,
  Conversation,
  ConversationSummary,
  AddConversationMessageInput,
  CreateConversationInput,
} from '../../domain/types';
import type { HttpClientOptions, IConversationsClient } from './types';
import { createHttpTransport, type HttpTransport } from './httpTransport';
import { parseEnvelope } from './parseEnvelope';

export class HttpConversationsClient implements IConversationsClient {
  private readonly transport: HttpTransport;

  constructor(options: HttpClientOptions | string = {}) {
    this.transport = createHttpTransport(options);
  }

  private async request<T>(path: string, options?: RequestInit): Promise<ApiEnvelope<T>> {
    const res = await this.transport.fetch(path, options);
    return parseEnvelope<T>(res, undefined, 'CONVERSATIONS_HTTP_ERROR');
  }

  getConversations(): Promise<ApiEnvelope<ConversationSummary[]>> {
    return this.request<ConversationSummary[]>('/api/conversations');
  }

  createConversation(input: CreateConversationInput): Promise<ApiEnvelope<Conversation>> {
    return this.request<Conversation>('/api/conversations', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  getConversation(id: string): Promise<ApiEnvelope<Conversation>> {
    return this.request<Conversation>(`/api/conversations/${encodeURIComponent(id)}`);
  }

  addMessage(id: string, message: AddConversationMessageInput): Promise<ApiEnvelope<Conversation>> {
    return this.request<Conversation>(`/api/conversations/${encodeURIComponent(id)}/messages`, {
      method: 'POST',
      body: JSON.stringify(message),
    });
  }

  deleteConversation(id: string): Promise<ApiEnvelope<{ deleted: boolean }>> {
    return this.request<{ deleted: boolean }>(`/api/conversations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }
}